/**
 * Domain theme system -- makes each domain look like a completely different website.
 *
 * Each theme defines: layout, typography, border radius, card style, button style,
 * header style, section backgrounds, and spacing. Domains get a theme assigned
 * via settings.theme_id (1-5). If not set, determined by crc32(domain) % 5.
 *
 * Fonts are loaded from Google Fonts in the root Blade template based on theme.
 */

export const THEMES = {
    // -----------------------------------------------------------------------
    // Theme 1: CLEAN -- minimal, airy, lots of whitespace, geometric sans
    // -----------------------------------------------------------------------
    1: {
        id: 1,
        name: 'clean',
        fonts: {
            heading: "'Inter', ui-sans-serif, system-ui, sans-serif",
            body: "'Inter', ui-sans-serif, system-ui, sans-serif",
            google: 'Inter:wght@400;500;600;700;800',
        },
        heroLayout: 'centered',       // form below hero text, centered
        headerStyle: 'light',          // white bg header
        radius: { card: '16px', button: '999px', input: '12px' },
        cardStyle: 'bordered',         // thin border, no shadow
        buttonStyle: 'pill',           // fully rounded buttons
        sectionBg: { primary: '#ffffff', secondary: '#f8fafc', dark: '#0f172a' },
        spacing: 'spacious',           // large padding between sections
        shadow: { card: 'none', panel: '0 1px 3px rgba(0,0,0,0.04)' },
    },

    // -----------------------------------------------------------------------
    // Theme 2: EDITORIAL -- serif headings, magazine feel, warm tones
    // -----------------------------------------------------------------------
    2: {
        id: 2,
        name: 'editorial',
        fonts: {
            heading: "'DM Serif Display', Georgia, serif",
            body: "'DM Sans', ui-sans-serif, system-ui, sans-serif",
            google: 'DM+Serif+Display:wght@400&family=DM+Sans:wght@400;500;600;700',
        },
        heroLayout: 'split',           // text left, form right
        headerStyle: 'cream',          // warm off-white header
        radius: { card: '8px', button: '6px', input: '6px' },
        cardStyle: 'shadow',           // soft shadow, no border
        buttonStyle: 'square',         // slightly rounded square
        sectionBg: { primary: '#fffbf5', secondary: '#f5f0e8', dark: '#1c1917' },
        spacing: 'normal',
        shadow: { card: '0 4px 20px rgba(0,0,0,0.06)', panel: '0 8px 30px rgba(0,0,0,0.08)' },
    },

    // -----------------------------------------------------------------------
    // Theme 3: TECH -- dark mode, sharp edges, futuristic, monospace accents
    // -----------------------------------------------------------------------
    3: {
        id: 3,
        name: 'tech',
        fonts: {
            heading: "'Space Grotesk', ui-sans-serif, system-ui, sans-serif",
            body: "'Space Grotesk', ui-sans-serif, system-ui, sans-serif",
            google: 'Space+Grotesk:wght@400;500;600;700',
        },
        heroLayout: 'fullwidth',       // full-width dark hero, form below
        headerStyle: 'dark',           // dark header
        radius: { card: '2px', button: '2px', input: '2px' },
        cardStyle: 'glass',            // glass/translucent cards
        buttonStyle: 'sharp',          // nearly square
        sectionBg: { primary: '#0a0a0a', secondary: '#111111', dark: '#000000' },
        spacing: 'compact',
        shadow: { card: '0 0 0 1px rgba(255,255,255,0.06)', panel: '0 0 0 1px rgba(255,255,255,0.08)' },
    },

    // -----------------------------------------------------------------------
    // Theme 4: FRIENDLY -- rounded, colorful, approachable, bouncy
    // -----------------------------------------------------------------------
    4: {
        id: 4,
        name: 'friendly',
        fonts: {
            heading: "'Outfit', ui-sans-serif, system-ui, sans-serif",
            body: "'Outfit', ui-sans-serif, system-ui, sans-serif",
            google: 'Outfit:wght@400;500;600;700;800',
        },
        heroLayout: 'stacked',         // hero text -> trust bar -> form in card
        headerStyle: 'colored',        // accent-colored header
        radius: { card: '24px', button: '999px', input: '16px' },
        cardStyle: 'filled',           // solid background cards
        buttonStyle: 'pill',
        sectionBg: { primary: '#ffffff', secondary: '#f0fdf4', dark: '#14532d' },
        spacing: 'spacious',
        shadow: { card: '0 8px 30px rgba(0,0,0,0.05)', panel: '0 12px 40px rgba(0,0,0,0.08)' },
    },

    // -----------------------------------------------------------------------
    // Theme 5: CLASSIC -- traditional business, boxed, conservative, serif
    // -----------------------------------------------------------------------
    5: {
        id: 5,
        name: 'classic',
        fonts: {
            heading: "'Source Serif 4', Georgia, serif",
            body: "'Source Sans 3', ui-sans-serif, system-ui, sans-serif",
            google: 'Source+Serif+4:wght@400;600;700&family=Source+Sans+3:wght@400;500;600;700',
        },
        heroLayout: 'boxed',           // contained hero with bordered form
        headerStyle: 'bordered',       // white header with bottom border
        radius: { card: '4px', button: '4px', input: '4px' },
        cardStyle: 'bordered',
        buttonStyle: 'square',
        sectionBg: { primary: '#ffffff', secondary: '#f9fafb', dark: '#1f2937' },
        spacing: 'normal',
        shadow: { card: '0 1px 2px rgba(0,0,0,0.05)', panel: '0 2px 8px rgba(0,0,0,0.06)' },
    },
};

/**
 * Get theme for a domain. Uses settings.theme_id if set, otherwise
 * deterministic hash of domain name.
 */
export function getThemeForDomain(domain) {
    const themeId = domain?.settings?.theme_id;
    if (themeId && THEMES[themeId]) {
        return THEMES[themeId];
    }

    // Deterministic assignment from domain name
    const name = domain?.name || domain?.request_host || 'default';
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = ((hash << 5) - hash + name.charCodeAt(i)) | 0;
    }
    const id = (Math.abs(hash) % 5) + 1;
    return THEMES[id];
}

/**
 * Convert theme to CSS custom properties for injection.
 */
export function themeToCssVars(theme) {
    if (!theme) return {};
    return {
        '--theme-font-heading': theme.fonts.heading,
        '--theme-font-body': theme.fonts.body,
        '--theme-radius-card': theme.radius.card,
        '--theme-radius-button': theme.radius.button,
        '--theme-radius-input': theme.radius.input,
        '--theme-shadow-card': theme.shadow.card,
        '--theme-shadow-panel': theme.shadow.panel,
        '--theme-bg-primary': theme.sectionBg.primary,
        '--theme-bg-secondary': theme.sectionBg.secondary,
        '--theme-bg-dark': theme.sectionBg.dark,
    };
}
